import React, { useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface HeroVideoProps {
    theme: number;
}

const HeroVideo: React.FC<HeroVideoProps> = ({ theme }) => {
    const { language } = useLanguage();
    const videoRef = useRef<HTMLVideoElement>(null);
    const t = (es: string, en: string) => language === 'es' ? es : en;

    // Videos para cada tema
    const getVideoSource = (theme: number) => {
        switch (theme) {
            case 1:
                return '/videos/libro1/VideoLibro1ES.mp4'; // General - usar español por defecto
            case 2:
                return language === 'es' ? '/videos/libro1/VideoLibro1ES.mp4' : '/videos/libro1/VideoLibro1EN.mp4';
            case 3:
                return language === 'es' ? '/videos/libro2/VideoLibro2ES.mp4' : '/videos/libro2/VideoLibro2EN.mp4';
            case 4:
                return language === 'es' ? '/videos/libro3/VideoLibro3ES.mp4' : '/videos/libro3/VideoLibro3EN.mp4';
            case 5:
                return language === 'es' ? '/videos/libro4/VideoLibro4ES.mp4' : '/videos/libro4/VideoLibro4EN.mp4';
            case 6:
                return language === 'es' ? '/videos/libro5/VideoLibro5ES.mp4' : '/videos/libro5/VideoLibro5EN.mp4';
            case 7:
                return language === 'es' ? '/videos/libro6/VideoLibro6ES.mp4' : '/videos/libro6/VideoLibro6EN.mp4';
            case 8:
                return language === 'es' ? '/videos/libro7/VideoLibro7ES.mp4' : '/videos/libro7/VideoLibro7EN.mp4';
            case 9:
                return language === 'es' ? '/videos/libro8/VideoLibro8ES.mp4' : '/videos/libro8/VideoLibro8EN.mp4';
            default:
                return '/videos/libro1/VideoLibro1ES.mp4';
        }
    };

    // Actualizar video cuando cambie el idioma
    useEffect(() => {
        if (videoRef.current) {
            const currentTime = videoRef.current.currentTime;
            const wasPaused = videoRef.current.paused;
            const wasMuted = videoRef.current.muted;
            
            videoRef.current.src = getVideoSource(theme);
            videoRef.current.currentTime = currentTime;
            videoRef.current.muted = wasMuted;
            
            if (!wasPaused) {
                videoRef.current.play();
            }
        }
    }, [language, theme]);

    const titles = {
        1: t('Transforma el Futuro de tus Hijos', 'Transform Your Children\'s Future'),
        2: t('Descubre el Mundo de la Verdad', 'Discover the World of Truth'),
        3: t('Explora los Secretos de la Luz', 'Explore the Secrets of Light'),
        4: t('Viaja por el Reino del Sonido', 'Travel Through the Kingdom of Sound'),
        5: t('Conoce las Leyes del Movimiento', 'Learn the Laws of Motion'),
        6: t('Experimenta con la Química', 'Experiment with Chemistry'),
        7: t('Descubre la Vida', 'Discover Life'),
        8: t('Explora la Electricidad', 'Explore Electricity'),
        9: t('Sumérgete en el Magnetismo', 'Dive into Magnetism'),
    };

    const descriptions = {
        1: t('Princonser Kids es la plataforma educativa que conecta padres, educadores y niños para crear experiencias de aprendizaje únicas y personalizadas.', 'Princonser Kids is the educational platform that connects parents, educators and children to create unique and personalized learning experiences.'),
        2: t('Aprende sobre la verdad, la honestidad y la importancia de ser auténtico en todas las situaciones.', 'Learn about truth, honesty and the importance of being authentic in all situations.'),
        3: t('Descubre cómo funciona la luz, los colores y las sombras en nuestro mundo.', 'Discover how light, colors and shadows work in our world.'),
        4: t('Explora el fascinante mundo del sonido y cómo viaja a través del aire.', 'Explore the fascinating world of sound and how it travels through the air.'),
        5: t('Aprende sobre el movimiento, la energía y las fuerzas que rigen nuestro universo.', 'Learn about motion, energy and the forces that govern our universe.'),
        6: t('Experimenta con reacciones químicas seguras y descubre cómo cambian las sustancias.', 'Experiment with safe chemical reactions and discover how substances change.'),
        7: t('Explora la vida, las plantas, los animales y los ecosistemas que nos rodean.', 'Explore life, plants, animals and the ecosystems that surround us.'),
        8: t('Descubre la electricidad, los circuitos y cómo funcionan los dispositivos electrónicos.', 'Discover electricity, circuits and how electronic devices work.'),
        9: t('Explora las fuerzas magnéticas y cómo los imanes interactúan con el mundo.', 'Explore magnetic forces and how magnets interact with the world.'),
    };

    return (
        <section className="relative min-h-screen overflow-hidden bg-black">
            {/* Video principal para Libros 1, 2, 3, 4, 5, 6, 7 y 8, imagen para el resto */}
            <div className="absolute inset-0 w-full h-full">
                {(theme === 2 || theme === 3 || theme === 4 || theme === 5 || theme === 6 || theme === 7 || theme === 8 || theme === 9) ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        playsInline
                        muted
                        className="w-full h-full object-contain"
                        poster="/images/castillobk.png"
                        onLoadedMetadata={() => {
                            if (videoRef.current) {
                                videoRef.current.volume = 0.5; // Volumen al 50%
                            }
                        }}
                    >
                        <source src={getVideoSource(theme)} type="video/mp4" />
                    </video>
                ) : (
                    <img
                        src="/images/castillobk.png"
                        alt="Princonser Kids"
                        className="w-full h-full object-cover"
                    />
                )}
            </div>

            {/* Controles de video en esquina inferior derecha - solo para Libros 1, 2, 3, 4, 5, 6, 7 y 8 */}
            {(theme === 2 || theme === 3 || theme === 4 || theme === 5 || theme === 6 || theme === 7 || theme === 8 || theme === 9) && (
                <div className="absolute bottom-6 right-6 flex gap-3 z-20">
                    {/* Botón de repetir */}
                    <button 
                        onClick={() => {
                            if (videoRef.current) {
                                videoRef.current.currentTime = 0;
                                videoRef.current.play();
                            }
                        }}
                        className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
                        title={t('Repetir', 'Repeat')}
                    >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                    </button>

                    {/* Botón de pausar/reproducir */}
                    <button 
                        onClick={() => {
                            if (videoRef.current) {
                                if (videoRef.current.paused) {
                                    videoRef.current.play();
                                } else {
                                    videoRef.current.pause();
                                }
                            }
                        }}
                        className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
                        title={t('Pausar/Reproducir', 'Pause/Play')}
                    >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                    </button>

                    {/* Botón de mutear/desmutear */}
                    <button 
                        onClick={() => {
                            if (videoRef.current) {
                                videoRef.current.muted = !videoRef.current.muted;
                            }
                        }}
                        className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
                        title={t('Mutear/Desmutear', 'Mute/Unmute')}
                    >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
        </section>
    );
};

export default HeroVideo; 